import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, ShoppingCart, Plus, Minus, Trash2,
  Pill, TestTube, Package, Wallet, Loader2, Check, AlertCircle,
  MapPin, User, Stethoscope, Building2, LocateFixed, QrCode, Sparkles
} from 'lucide-react';
import AddressPickerCheckout from './AddressPickerCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientMarketplacePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [productType, setProductType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [wallet, setWallet] = useState({ balance: 0 });
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [doctorSearch, setDoctorSearch] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deliveryPhone, setDeliveryPhone] = useState('');

  // Location and filter state
  const [userLocation, setUserLocation] = useState(null);
  const [maxKm, setMaxKm] = useState('');
  const [sortBy, setSortBy] = useState('');

  // Prescription scan state
  const [rxScanning, setRxScanning] = useState(false);
  const [rxData, setRxData] = useState(null);
  const [showRxResults, setShowRxResults] = useState(false);

  const getToken = () => localStorage.getItem('patient_token');

  // Fetch all doctors for prescribing selection (with search)
  const fetchDoctors = useCallback(async (search = '') => {
    const token = getToken();
    if (!token) return;
    
    try {
      const params = new URLSearchParams({ include_all: 'true' });
      if (search) params.append('search', search);
      
      const res = await fetch(`${API_URL}/api/patient-health/my-doctors?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDoctors(data.doctors || []);
      }
    } catch (err) {
      console.error('Failed to fetch doctors:', err);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/patient/login');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (productType && productType !== 'all') params.append('type', productType);
      if (userLocation?.lat) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      if (maxKm) params.append('max_km', maxKm);
      if (sortBy) params.append('sort_by', sortBy);

      // Fetch products and wallet in parallel
      const [productsRes, walletRes] = await Promise.all([
        fetch(`${API_URL}/api/patient-marketplace/products?${params}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-wallet/balance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);

      if (productsRes?.ok) { const d = await productsRes.json(); setProducts(d.products || []); }
      if (walletRes?.ok) setWallet(await walletRes.json());
      
      // Fetch doctors separately
      await fetchDoctors();

    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load products');
    }
    setLoading(false);
  }, [navigate, searchQuery, productType, fetchDoctors, userLocation, maxKm, sortBy]);

  // Debounced doctor search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showCheckout) {
        fetchDoctors(doctorSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [doctorSearch, showCheckout, fetchDoctors]);

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          // Use reverse geocoding to get address
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          const address = data.display_name || `${latitude}, ${longitude}`;
          
          setDeliveryLocation({ lat: latitude, lng: longitude, formatted_address: address });
          setDeliveryAddress(address);
          toast.success('Location fetched successfully');
        } catch (err) {
          // Fallback to coordinates only
          setDeliveryLocation({ lat: latitude, lng: longitude, formatted_address: `${latitude}, ${longitude}` });
          setDeliveryAddress(`${latitude}, ${longitude}`);
        }
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Please enable location access in your browser settings');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information is unavailable');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out');
            break;
          default:
            toast.error('Failed to get your location');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('patient_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Handle prescription QR scan (from URL param or localStorage)
  useEffect(() => {
    const rxBarcode = searchParams.get('rx') || localStorage.getItem('pending_rx_barcode');
    if (rxBarcode && !rxScanning && !rxData) {
      localStorage.removeItem('pending_rx_barcode');
      scanPrescription(rxBarcode);
    }
  }, [searchParams]);

  const scanPrescription = async (barcode) => {
    const token = getToken();
    if (!token) return;
    setRxScanning(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-marketplace/scan-prescription/${barcode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.found) {
        setRxData(data);
        setShowRxResults(true);

        // Auto-set the prescribing doctor
        if (data.doctor_id) {
          setSelectedDoctor(data.doctor_id);
        }

        // Auto-add matched products to cart from the best chemist
        if (data.chemist_matches?.length > 0) {
          const bestChemist = data.chemist_matches[0];
          const newCartItems = [];
          for (const match of bestChemist.matches) {
            if (match.matched && match.catalog_product) {
              const p = match.catalog_product;
              newCartItems.push({
                id: p.id,
                type: 'medicine',
                name: p.name,
                generic_name: p.generic_name || '',
                mrp: p.patient_mrp || p.mrp || 0,
                patient_price: p.patient_mrp || p.mrp || 0,
                vendor_id: p.vendor_id || bestChemist.vendor_id,
                vendor_name: bestChemist.vendor_name,
                quantity: 1,
              });
            }
          }
          if (newCartItems.length > 0) {
            setCart(newCartItems);
            toast.success(`${newCartItems.length} medicines added to cart from ${bestChemist.vendor_name}`);
          }
        } else {
          toast.info('Prescription found but no matching medicines in nearby chemists');
        }
      } else {
        toast.error('Prescription not found for this QR code');
      }
    } catch (err) {
      console.error('Scan error:', err);
      toast.error('Failed to scan prescription');
    }
    setRxScanning(false);
  };

  useEffect(() => {
    // Save cart to localStorage
    localStorage.setItem('patient_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success('Added to cart');
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === productId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.patient_price || item.mrp) * item.quantity, 0);
  // Calculate GST per item using vendor's GST percentage
  const gstAmount = cart.reduce((sum, item) => {
    const itemTotal = (item.patient_price || item.mrp) * item.quantity;
    const gstPct = item.vendor_gst_percentage ?? 18;
    return sum + Math.round(itemTotal * gstPct / 100);
  }, 0);
  const grandTotal = cartTotal + gstAmount;

  const handleCheckout = async () => {
    if (!selectedDoctor) {
      toast.error('Please select your prescribing doctor or choose "Self (Not prescribed by doctor)"');
      return;
    }

    if (wallet.balance < grandTotal) {
      toast.error('Insufficient wallet balance');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-marketplace/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          items: cart.map(item => ({
            product_id: item.id,
            product_type: item.type,
            quantity: item.quantity
          })),
          prescribing_doctor_id: selectedDoctor,
          delivery_address: deliveryAddress,
          delivery_location: deliveryLocation,
          delivery_phone: deliveryPhone,
          notes: notes
        })
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Order placed successfully!');
        setCart([]);
        localStorage.removeItem('patient_cart');
        setShowCheckout(false);
        navigate('/patient/dashboard');
      } else {
        toast.error(data.detail || 'Failed to place order');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setSubmitting(false);
  };

  const getProductIcon = (type) => {
    switch (type) {
      case 'medicine': return <Pill className="h-5 w-5 text-purple-600" />;
      case 'lab_test': return <TestTube className="h-5 w-5 text-orange-600" />;
      default: return <Package className="h-5 w-5 text-blue-600" />;
    }
  };

  // Group products by vendor for price comparison view
  const getProductsByVendor = () => {
    const vendorMap = {};
    products.forEach(product => {
      const vendorId = product.vendor_id || 'unknown';
      const vendorName = product.vendor_name || 'Unknown Vendor';
      if (!vendorMap[vendorId]) {
        vendorMap[vendorId] = {
          id: vendorId,
          name: vendorName,
          products: []
        };
      }
      vendorMap[vendorId].products.push(product);
    });
    return Object.values(vendorMap).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Group products by name for price comparison
  const getProductsForComparison = () => {
    const productMap = {};
    products.forEach(product => {
      const key = product.name.toLowerCase().trim();
      if (!productMap[key]) {
        productMap[key] = {
          name: product.name,
          type: product.type,
          vendors: []
        };
      }
      productMap[key].vendors.push({
        ...product,
        vendor_name: product.vendor_name || 'Unknown Vendor'
      });
    });
    // Sort vendors by price within each product
    Object.values(productMap).forEach(p => {
      p.vendors.sort((a, b) => (a.patient_price || a.mrp) - (b.patient_price || b.mrp));
    });
    return Object.values(productMap);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/patient/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-bold text-lg">Marketplace</h1>
              <p className="text-sm text-muted-foreground">Order medicines and lab tests</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/patient/wallet')}>
              <Wallet className="h-4 w-4 mr-2" />
              ₹{wallet.balance?.toFixed(0) || 0}
            </Button>
            
            <Button
              variant={cart.length > 0 ? 'default' : 'outline'}
              onClick={() => setShowCheckout(true)}
              disabled={cart.length === 0}
              className="relative"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Cart ({cart.length})
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search & Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search medicines, lab tests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={productType} onValueChange={setProductType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="medicine">Medicines</SelectItem>
              <SelectItem value="lab_test">Lab Tests</SelectItem>
            </SelectContent>
          </Select>
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-48" data-testid="view-mode-select">
              <SelectValue placeholder="View Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="grid">All Products</SelectItem>
              <SelectItem value="vendor">By Vendor</SelectItem>
              <SelectItem value="compare">Compare Prices</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Location & Distance Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg border" data-testid="location-filters">
          <Button
            size="sm"
            variant={userLocation ? 'default' : 'outline'}
            onClick={() => {
              if (userLocation) { setUserLocation(null); return; }
              if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                  toast.success('Location detected! Showing nearby vendors.');
                },
                () => toast.error('Location access denied'),
                { enableHighAccuracy: true, timeout: 10000 }
              );
            }}
            data-testid="detect-location-btn"
          >
            <LocateFixed className="h-3.5 w-3.5 mr-1" />
            {userLocation ? 'Location On' : 'Detect Location'}
          </Button>
          {userLocation && (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500">Within</span>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  placeholder="km"
                  value={maxKm}
                  onChange={e => setMaxKm(e.target.value)}
                  className="w-16 h-8 text-xs"
                  data-testid="max-km-input"
                />
                <span className="text-xs text-slate-500">km</span>
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="sort-by-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nearest">Nearest First</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
        </div>

        {/* Prescription Scan Loading Overlay */}
        {rxScanning && (
          <Card className="mb-6 border-teal-200 bg-teal-50">
            <CardContent className="py-6 flex items-center justify-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
              <div>
                <p className="font-medium text-teal-800">Scanning prescription...</p>
                <p className="text-sm text-teal-600">AI is matching your medicines with nearby chemists</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Prescription Scan Results */}
        {rxData && showRxResults && (
          <Card className="mb-6 border-teal-300 shadow-md" data-testid="rx-scan-results">
            <CardHeader className="pb-2 bg-gradient-to-r from-teal-50 to-emerald-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-teal-600" />
                  Prescription Detected
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowRxResults(false)}>Dismiss</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <div><span className="text-slate-500">Patient:</span> <strong>{rxData.patient_name}</strong></div>
                <div><span className="text-slate-500">Doctor:</span> <strong>{rxData.doctor_name}</strong></div>
                {rxData.diagnosis?.length > 0 && (
                  <div><span className="text-slate-500">Diagnosis:</span> {rxData.diagnosis.join(', ')}</div>
                )}
              </div>

              <div className="text-sm font-medium text-slate-700">
                Prescribed: {rxData.medications?.map(m => m.name || m.medicine).filter(Boolean).join(', ')}
              </div>

              {rxData.chemist_matches?.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-teal-700">Nearby chemists with your medicines:</p>
                  {rxData.chemist_matches.slice(0, 5).map((ch, idx) => (
                    <div key={ch.vendor_id} className={`p-3 rounded-lg border flex items-center justify-between ${idx === 0 ? 'bg-teal-50 border-teal-200' : 'bg-white'}`}>
                      <div>
                        <p className="font-medium text-sm flex items-center gap-1">
                          <Building2 className="h-4 w-4 text-teal-600" />
                          {ch.vendor_name}
                          {idx === 0 && <Badge className="ml-1 bg-teal-600 text-xs">Best Match</Badge>}
                        </p>
                        <p className="text-xs text-slate-500">
                          {ch.matched_count}/{ch.total_prescribed} medicines available
                          {ch.address && ` · ${ch.address}`}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant={idx === 0 ? 'default' : 'outline'}
                        onClick={() => {
                          // Add this chemist's matched products to cart
                          const newItems = ch.matches
                            .filter(m => m.matched && m.catalog_product)
                            .map(m => ({
                              id: m.catalog_product.id,
                              type: 'medicine',
                              name: m.catalog_product.name,
                              generic_name: m.catalog_product.generic_name || '',
                              mrp: m.catalog_product.patient_mrp || m.catalog_product.mrp || 0,
                              patient_price: m.catalog_product.patient_mrp || m.catalog_product.mrp || 0,
                              vendor_id: m.catalog_product.vendor_id || ch.vendor_id,
                              vendor_name: ch.vendor_name,
                              quantity: 1,
                            }));
                          setCart(newItems);
                          toast.success(`Cart updated with ${newItems.length} items from ${ch.vendor_name}`);
                        }}
                        data-testid={`select-chemist-${ch.vendor_id}`}
                      >
                        <ShoppingCart className="h-3 w-3 mr-1" /> Select
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  No chemists found with matching medicines. Browse products below.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Products Display */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No products found</p>
          </div>
        ) : viewMode === 'vendor' ? (
          /* Vendor-wise View */
          <div className="space-y-6">
            {getProductsByVendor().map((vendor) => (
              <Card key={vendor.id} className="overflow-hidden">
                <CardHeader className="bg-slate-50 py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-teal-600" />
                    {vendor.name}
                    <Badge variant="outline" className="ml-2">{vendor.products.length} products</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {vendor.products.map((product) => (
                      <div key={product.id} className="p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1">
                            {getProductIcon(product.type)}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{product.name}</p>
                              {product.generic_name && (
                                <p className="text-xs text-muted-foreground truncate">{product.generic_name}</p>
                              )}
                              <Badge variant="outline" className="text-xs mt-1">
                                {product.type === 'medicine' ? 'Medicine' : 'Lab Test'}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-teal-600">₹{product.patient_price || product.mrp}</p>
                            {product.patient_price && product.patient_price < product.mrp && (
                              <p className="text-xs text-muted-foreground line-through">₹{product.mrp}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          {cart.find(item => item.id === product.id) ? (
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQuantity(product.id, -1)}>
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center text-sm">{cart.find(item => item.id === product.id)?.quantity}</span>
                              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQuantity(product.id, 1)}>
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => addToCart(product)}>
                              <Plus className="h-3 w-3 mr-1" /> Add
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : viewMode === 'compare' ? (
          /* Price Comparison View */
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Compare prices from different vendors for the same product
            </p>
            {getProductsForComparison().map((item) => (
              <Card key={item.name} className="overflow-hidden">
                <CardHeader className="py-3 bg-slate-50">
                  <CardTitle className="text-base flex items-center gap-2">
                    {getProductIcon(item.type)}
                    {item.name}
                    <Badge variant="outline">{item.vendors.length} vendors</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {item.vendors.map((product, idx) => (
                      <div key={product.id} className={`p-3 flex items-center justify-between ${idx === 0 ? 'bg-green-50' : ''}`}>
                        <div className="flex items-center gap-3">
                          {idx === 0 && <Badge className="bg-green-600">Lowest</Badge>}
                          <div>
                            <p className="font-medium text-sm flex items-center gap-1">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              {product.vendor_name}
                              {product.vendor_distance_km != null && (
                                <span className="text-xs text-slate-400 ml-1">({product.vendor_distance_km} km)</span>
                              )}
                              {product.vendor_is_open === false && (
                                <Badge variant="outline" className="text-xs text-red-500 border-red-300 ml-1">Closed</Badge>
                              )}
                            </p>
                            {product.generic_name && (
                              <p className="text-xs text-muted-foreground">{product.generic_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className={`font-bold ${idx === 0 ? 'text-green-600' : 'text-slate-700'}`}>
                              ₹{product.patient_price || product.mrp}
                            </p>
                            {product.patient_price && product.patient_price < product.mrp && (
                              <p className="text-xs text-muted-foreground line-through">₹{product.mrp}</p>
                            )}
                          </div>
                          {cart.find(i => i.id === product.id) ? (
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQuantity(product.id, -1)}>
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center text-sm">{cart.find(i => i.id === product.id)?.quantity}</span>
                              <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => updateQuantity(product.id, 1)}>
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" onClick={() => addToCart(product)}>
                              <Plus className="h-3 w-3 mr-1" /> Add
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Default Grid View */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <Card key={product.id} className="hover:shadow-md transition-shadow min-w-0">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      {getProductIcon(product.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{product.name}</p>
                          {product.generic_name && (
                            <p className="text-xs text-muted-foreground truncate">{product.generic_name}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {product.type === 'medicine' ? 'Medicine' : 'Lab Test'}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-1">
                        {product.vendor_name}
                        {product.vendor_distance_km != null && <span className="text-teal-600 ml-1">· {product.vendor_distance_km} km</span>}
                        {product.vendor_is_open === false && <span className="text-red-500 ml-1">· Closed</span>}
                      </p>
                      
                      {product.type === 'lab_test' && (
                        <div className="flex gap-2 mt-2">
                          {product.sample_type && <Badge variant="secondary" className="text-xs">{product.sample_type}</Badge>}
                          {product.turnaround_time && <Badge variant="secondary" className="text-xs">{product.turnaround_time}</Badge>}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-3">
                        <div>
                          <p className="text-lg font-bold text-teal-600">₹{product.patient_price || product.mrp}</p>
                          {product.patient_price && product.patient_price < product.mrp && (
                            <p className="text-xs text-muted-foreground line-through">₹{product.mrp}</p>
                          )}
                        </div>
                        
                        {cart.find(item => item.id === product.id) ? (
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(product.id, -1)}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-medium">
                              {cart.find(item => item.id === product.id)?.quantity || 0}
                            </span>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(product.id, 1)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" onClick={() => addToCart(product)}>
                            <Plus className="h-4 w-4 mr-1" /> Add
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Checkout</DialogTitle>
          </DialogHeader>

          {/* Cart Items */}
          <div className="space-y-3 mb-4">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {getProductIcon(item.type)}
                  <div>
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.vendor_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateQuantity(item.id, -1)}>
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-6 text-center text-sm">{item.quantity}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateQuantity(item.id, 1)}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="font-medium w-16 text-right">₹{((item.patient_price || item.mrp) * item.quantity).toFixed(0)}</p>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => removeFromCart(item.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Prescribing Doctor Selection */}
          <div className="space-y-2 mb-4">
            <label className="text-sm font-medium flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-teal-600" />
              Prescribing Doctor *
            </label>
            
            {/* Doctor Search & Select Combined */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search doctors by name, specialty, or clinic..."
                  value={doctorSearch}
                  onChange={(e) => setDoctorSearch(e.target.value)}
                  className="pl-10"
                  data-testid="doctor-search-input"
                />
              </div>
              
              {/* Doctor List - scrollable */}
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {/* Self Option */}
                <div 
                  className={`p-3 cursor-pointer hover:bg-slate-50 border-b ${selectedDoctor === 'self' ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''}`}
                  onClick={() => setSelectedDoctor('self')}
                  data-testid="doctor-option-self"
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <div>
                      <span className="font-medium">Self (Not prescribed by doctor)</span>
                      <p className="text-xs text-muted-foreground">Choose this if ordering without a prescription</p>
                    </div>
                    {selectedDoctor === 'self' && <Check className="h-4 w-4 text-teal-600 ml-auto" />}
                  </div>
                </div>
                
                {/* Doctor List */}
                {doctors.length === 0 && !doctorSearch ? (
                  <div className="p-3 text-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Loading doctors...
                  </div>
                ) : doctors.length === 0 && doctorSearch ? (
                  <div className="p-3 text-center text-sm text-muted-foreground">
                    No doctors found for "{doctorSearch}"
                  </div>
                ) : (
                  doctors.map((doc) => (
                    <div 
                      key={doc.id}
                      className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${selectedDoctor === doc.id ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''}`}
                      onClick={() => setSelectedDoctor(doc.id)}
                      data-testid={`doctor-option-${doc.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <Stethoscope className="h-4 w-4 text-teal-600 mt-0.5" />
                        <div className="flex-1">
                          <span className="font-medium">{doc.name}</span>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {doc.specialty && (
                              <span className="text-teal-600">{doc.specialty}</span>
                            )}
                            {doc.clinic_name && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {doc.clinic_name}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedDoctor === doc.id && <Check className="h-4 w-4 text-teal-600" />}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Selected Doctor Display */}
              {selectedDoctor && (
                <div className="flex items-center gap-2 p-2 bg-teal-50 rounded-md text-sm">
                  <Check className="h-4 w-4 text-teal-600" />
                  <span className="text-teal-700 font-medium">
                    Selected: {selectedDoctor === 'self' ? 'Self (No prescription)' : doctors.find(d => d.id === selectedDoctor)?.name || 'Unknown'}
                  </span>
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              Select the doctor who prescribed these medicines/tests, or choose "Self" if not prescribed
            </p>
          </div>

          {/* Delivery Address with Location */}
          <div className="space-y-2 mb-4">
            <label className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4 text-teal-600" />
              Delivery Address & Phone
            </label>
            <AddressPickerCheckout
              token={getToken()}
              initialAddress={deliveryAddress}
              initialPhone=""
              initialLocation={deliveryLocation}
              onAddressSelected={(data) => {
                setDeliveryAddress(data.address || '');
                setDeliveryLocation(data.location || null);
                setDeliveryPhone(data.phone || '');
              }}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2 mb-4">
            <label className="text-sm font-medium">Notes (Optional)</label>
            <Input
              placeholder="Any special instructions"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Order Summary */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>₹{cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>GST ({cart.length > 0 ? (cart[0].vendor_gst_percentage ?? 18) : 18}%)</span>
              <span>₹{gstAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Wallet Balance Check */}
          <div className={`flex items-center gap-2 p-3 rounded-lg ${wallet.balance >= grandTotal ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {wallet.balance >= grandTotal ? (
              <>
                <Check className="h-5 w-5" />
                <span>Wallet Balance: ₹{wallet.balance.toFixed(2)}</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5" />
                <span>Insufficient balance. Add ₹{(grandTotal - wallet.balance).toFixed(2)} more</span>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCheckout}
              disabled={submitting || !selectedDoctor || wallet.balance < grandTotal}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Pay ₹{grandTotal.toFixed(0)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
